'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useCart } from '../contexts/CartContext';
import { generateProductSlug } from '@shared/lib/slugify';
import { getProductPrice } from '@shared/lib/schema-helpers';
import { PRODUCT_PLACEHOLDER_IMAGE } from '@shared/lib/product-placeholder';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type SizeOption = {
  productId: string;
  sizeOrWeight: string | null;
  price: number;
  inStock: number;
  variationId: string | null;
};

type Product = {
  id: string;
  name: string;
  slug: string | null;
  brandName: string | null;
  classificationName?: string | null;
  sizeOrWeight?: string | null;
  heroShotUri: string | null;
  shortDescription: string | null;
  price: number | null;
  inStock: number;
  image: string | null;
  groupProducts?: Product[];
  sizeOptions?: SizeOption[];
  isGrouped?: boolean;
  prices?: Array<{ price: number; covaLocationId: number }>;
  availabilities?: Array<{ inStock: number; covaLocationId: number }>;
};

type CollectionEmbedProps = {
  collectionId: string;
  displayType?: 'CAROUSEL' | 'GRID' | 'LIST';
  productLimit?: number;
  title?: string | null;
  showTitle?: boolean;
};

export default function CollectionEmbed({
  collectionId,
  displayType = 'CAROUSEL',
  productLimit = 12,
  title,
  showTitle = true,
}: CollectionEmbedProps) {
  const { state } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [collectionTitle, setCollectionTitle] = useState<string | null>(null);

  useEffect(() => {
    const loadCollection = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          limit: String(productLimit),
        });

        // Add location context if available
        if (state.storeCovaLocationId) {
          params.set('covaLocationId', String(state.storeCovaLocationId));
        }
        if (state.fulfillmentMode) {
          params.set('locationType', state.fulfillmentMode === 'delivery' ? 'same-day' : 'store');
        }

        const res = await fetch(`${API_BASE}/api/collection-embeds/${collectionId}?${params.toString()}`);
        const data = await res.json();

        if (data.success) {
          // Transform grouped products to display format
          const transformedProducts = (data.products || []).map((product: Product) => {
            // Get price from sizeOptions or prices array
            let price: number | null = null;
            if (product.sizeOptions && product.sizeOptions.length > 0) {
              const firstAvailable = product.sizeOptions.find(opt => opt.inStock >= 1) || product.sizeOptions[0];
              price = firstAvailable.price;
            } else {
              // Prices are now in Availability.price field (consolidated from ProductPrice)
              const priceFromAvail = getProductPrice(product.availabilities);
              if (priceFromAvail !== null) {
                price = priceFromAvail;
            } else if (product.price !== null && product.price !== undefined) {
              price = product.price;
              }
            }

            // Get stock from sizeOptions or availabilities
            let inStock = 0;
            if (product.sizeOptions && product.sizeOptions.length > 0) {
              inStock = product.sizeOptions.reduce((sum, opt) => sum + opt.inStock, 0);
            } else if (product.availabilities && product.availabilities.length > 0) {
              inStock = product.availabilities.reduce((sum, a) => sum + Number(a.inStock || 0), 0);
            } else {
              inStock = product.inStock || 0;
            }

            // Get image from first size option or product
            let image = product.image || product.heroShotUri;
            if (product.sizeOptions && product.sizeOptions.length > 0) {
              const firstOption = product.sizeOptions[0];
              // Try to get image from groupProducts if available
              if (product.groupProducts && product.groupProducts.length > 0) {
                const matchingProduct = product.groupProducts.find(p => p.id === firstOption.productId);
                if (matchingProduct?.heroShotUri) {
                  image = matchingProduct.heroShotUri;
                }
              }
            }

            return {
              ...product,
              price,
              inStock,
              image,
            };
          });
          
          setProducts(transformedProducts);
          setCollectionTitle(data.collection?.title || title || null);
        }
      } catch (error) {
        console.error('Failed to load collection embed:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCollection();
  }, [collectionId, productLimit, state.storeCovaLocationId, state.fulfillmentMode, title]);

  if (loading) {
    return (
      <div className="py-8">
        <div className="text-center text-text-tertiary">Loading collection...</div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  const displayTitle = showTitle ? (title || collectionTitle) : null;

  if (displayType === 'CAROUSEL') {
    return (
      <div className="py-8">
        {displayTitle && (
          <h2 className="text-h3-mobile md:text-h4-desktop font-bold text-text-primary mb-6">{displayTitle}</h2>
        )}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-4">
            {products.map((product) => {
              const productSlug = product.slug || generateProductSlug({
                name: product.name,
                id: product.id,
                brandName: product.brandName || undefined,
                classificationName: product.classificationName || undefined,
                sizeOrWeight: product.sizeOrWeight || undefined,
              });
              const productUrl = `/product/${productSlug}?variant=${encodeURIComponent(productSlug)}`;
              
              return (
                <Link
                  key={product.id}
                  href={productUrl}
                  className="flex-shrink-0 w-64 group"
                >
                  <div className=" rounded-lg border border-neutral-20 overflow-hidden transition-shadow">
                    <div className="relative aspect-square bg-bg-2">
                      <Image
                        src={product.image || product.heroShotUri || PRODUCT_PLACEHOLDER_IMAGE}
                        alt={product.name}
                        fill
                        className={`object-cover ${product.image || product.heroShotUri ? "" : "p-4 object-contain opacity-60"}`}
                        sizes="256px"
                        unoptimized={!product.image && !product.heroShotUri}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-text-primary group- transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      {product.brandName && (
                        <p className="text-sm text-text-tertiary mt-1">{product.brandName}</p>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {product.price !== null && (
                          <span className="text-lg font-semibold text-text-primary">${product.price.toFixed(2)}</span>
                        )}
                        {product.inStock > 0 && (
                          <span className="text-xs text-text-tertiary shrink-0">{product.inStock} available</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (displayType === 'GRID') {
    return (
      <div className="py-8">
        {displayTitle && (
          <h2 className="text-h3-mobile md:text-h4-desktop font-bold text-text-primary mb-6">{displayTitle}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((product) => {
            const productSlug = product.slug || generateProductSlug({
              name: product.name,
              id: product.id,
              brandName: product.brandName || undefined,
            });
            const productUrl = `/product/${productSlug}?variant=${encodeURIComponent(productSlug)}`;
            
            return (
              <Link
                key={product.id}
                href={productUrl}
                className="group"
              >
                <div className=" rounded-lg border border-neutral-20 overflow-hidden transition-shadow">
                  <div className="relative aspect-square bg-bg-2">
                    <Image
                      src={product.image || product.heroShotUri || PRODUCT_PLACEHOLDER_IMAGE}
                      alt={product.name}
                      fill
                      className={`object-cover ${product.image || product.heroShotUri ? "" : "p-4 object-contain opacity-60"}`}
                      sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      unoptimized={!product.image && !product.heroShotUri}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-text-primary group- transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    {product.brandName && (
                      <p className="text-sm text-text-tertiary mt-1">{product.brandName}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2">
                      {product.price !== null && (
                        <span className="text-lg font-semibold text-text-primary">${product.price.toFixed(2)}</span>
                      )}
                      {product.inStock > 0 && (
                        <span className="text-xs text-text-tertiary shrink-0">{product.inStock} available</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // LIST display type
  return (
    <div className="py-8">
      {displayTitle && (
        <h2 className="text-h3-mobile md:text-h4-desktop font-bold text-text-primary mb-6">{displayTitle}</h2>
      )}
      <div className="space-y-4">
        {products.map((product) => {
          const productSlug = product.slug || generateProductSlug({
            name: product.name,
            id: product.id,
            brandName: product.brandName || undefined,
          });
          const productUrl = `/product/${productSlug}?variant=${encodeURIComponent(productSlug)}`;
          
          return (
            <Link
              key={product.id}
              href={productUrl}
              className="flex gap-4 rounded-lg border border-neutral-20 p-4 transition-shadow group"
            >
              <div className="relative w-24 h-24 flex-shrink-0 bg-bg-2 rounded">
                <Image
                  src={product.image || product.heroShotUri || PRODUCT_PLACEHOLDER_IMAGE}
                  alt={product.name}
                  fill
                  className={`rounded object-cover ${product.image || product.heroShotUri ? "" : "p-2 object-contain opacity-60"}`}
                  sizes="96px"
                  unoptimized={!product.image && !product.heroShotUri}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-text-primary group- transition-colors">
                  {product.name}
                </h3>
                {product.brandName && (
                  <p className="text-sm text-text-tertiary mt-1">{product.brandName}</p>
                )}
                {product.shortDescription && (
                  <p className="text-sm text-text-secondary mt-2 line-clamp-2">{product.shortDescription}</p>
                )}
              </div>
              <div className="flex flex-col items-end justify-between gap-1 text-right">
                {product.price !== null && (
                  <span className="text-lg font-semibold text-text-primary">${product.price.toFixed(2)}</span>
                )}
                {product.inStock > 0 && (
                  <span className="text-xs text-text-tertiary">{product.inStock} available</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

